import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { type Event } from 'nostr-tools';
import { MessageCircle, Send, Loader2, CornerDownRight, ChevronDown, ChevronUp, Crown } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import MentionTextarea from './MentionTextarea';
import NoteContent from './NoteContent';
import { timeAgo } from '../lib/utils';
import { safeUrl } from '../lib/podcast';

// ── Campaign Q&A / Comments ─────────────────────────────────────────
//
// A discussion thread attached to a Creator Goal so supporters can ask
// questions in one place. Uses NIP-22 kind 1111 comments, scoped to the
// goal's ADDRESSABLE coordinate (`a` = 30078:<pubkey>:<d-tag>). Scoping to
// the coordinate (not a specific event id) means the thread survives goal
// edits (which mint a new event id) and stays attached to the campaign.
//
// Root scope tags (uppercase) point at the goal; parent tags (lowercase)
// point at either the goal (top-level comment) or another comment (reply).

const COMMENT_KIND = 1111;
const GOAL_ROOT_KIND = 30078;

interface CampaignCommentsProps {
  /** The goal event these comments are attached to. */
  goalEvent: Event;
  /** The creator (campaign owner) pubkey — highlighted as OP in the thread. */
  creatorPubkey: string;
  /**
   * The campaign's STABLE id (the goal's original createdAt). Used to scope
   * the thread so it stays continuous across the campaign's lifecycle — the
   * active goal (fixed d-tag) and its archived snapshot (unique d-tag) resolve
   * to the SAME comment thread. Falls back to the event's own d-tag coordinate
   * when not provided.
   */
  campaignId?: number;
}

/**
 * Build the addressable coordinate the thread is scoped to. Prefer a stable
 * per-campaign d-tag (`bchnostr/creator-goal/<campaignId>`) so comments follow
 * the campaign from active → completed/archived. This is a virtual coordinate
 * used only as a NIP-22 root scope; no event is published under it.
 */
function campaignCoordinate(ev: Event, campaignId?: number): string {
  if (campaignId) {
    return `${GOAL_ROOT_KIND}:${ev.pubkey}:bchnostr/creator-goal/${campaignId}`;
  }
  const d = ev.tags.find(t => t[0] === 'd')?.[1] ?? '';
  return `${GOAL_ROOT_KIND}:${ev.pubkey}:${d}`;
}

export default function CampaignComments({ goalEvent, creatorPubkey, campaignId }: CampaignCommentsProps) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const mentioned = useMemo(() => new Set<string>(), []);

  const coord = useMemo(() => campaignCoordinate(goalEvent, campaignId), [goalEvent, campaignId]);

  // Fetch every comment scoped to this campaign coordinate. `#a` catches all
  // comments whose root/parent points at the goal coordinate across relays.
  const { events, loading, refetch } = useNostrQuery({
    filters: [{ kinds: [COMMENT_KIND], '#a': [coord], limit: 500 }],
    enabled: true,
    allRelays: true,
  });

  // Build a top-level list + a parent→children map (one level of nesting is
  // rendered; deeper replies collapse under their nearest shown ancestor).
  const { topLevel, childrenOf } = useMemo(() => {
    const byId = new Map<string, Event>();
    for (const ev of events) byId.set(ev.id, ev);
    const all = [...byId.values()].sort((a, b) => a.created_at - b.created_at);

    const children = new Map<string, Event[]>();
    const roots: Event[] = [];
    for (const ev of all) {
      // The lowercase `e` tag is the parent when replying to another comment.
      const parentE = ev.tags.find(t => t[0] === 'e')?.[1];
      if (parentE && byId.has(parentE)) {
        const arr = children.get(parentE) ?? [];
        arr.push(ev);
        children.set(parentE, arr);
      } else {
        roots.push(ev);
      }
    }
    return { topLevel: roots, childrenOf: children };
  }, [events]);

  const count = events.length;

  const publishComment = useCallback(async (
    body: string,
    parent: Event | null,
  ) => {
    const content = body.trim();
    if (!content) return;
    if (!user) { setShowLoginDialog(true); return; }
    setPosting(true);
    try {
      // Root scope (uppercase) always points at the goal.
      const tags: string[][] = [
        ['A', coord, ''],
        ['E', goalEvent.id, '', goalEvent.pubkey],
        ['K', String(GOAL_ROOT_KIND)],
        ['P', creatorPubkey],
      ];

      if (parent) {
        // Reply to a comment: parent (lowercase) points at that comment.
        tags.push(['e', parent.id, '', parent.pubkey]);
        tags.push(['k', String(COMMENT_KIND)]);
        tags.push(['p', parent.pubkey]);
      } else {
        // Top-level comment: parent == root (the goal).
        tags.push(['a', coord, '']);
        tags.push(['e', goalEvent.id, '', goalEvent.pubkey]);
        tags.push(['k', String(GOAL_ROOT_KIND)]);
        tags.push(['p', creatorPubkey]);
      }

      // Carry any @-mentioned pubkeys as extra p tags for notifications.
      for (const pk of mentioned) {
        if (!tags.some(t => (t[0] === 'p' || t[0] === 'P') && t[1] === pk)) {
          tags.push(['p', pk]);
        }
      }

      await publish({ kind: COMMENT_KIND, content, tags, created_at: Math.floor(Date.now() / 1000) });
      refetch();
    } finally {
      setPosting(false);
    }
  }, [user, setShowLoginDialog, coord, goalEvent, creatorPubkey, mentioned, publish, refetch]);

  const handlePost = useCallback(async () => {
    await publishComment(text, null);
    setText('');
    mentioned.clear();
  }, [publishComment, text, mentioned]);

  return (
    <div className="border-t border-border">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold">
          <MessageCircle className="w-4 h-4 text-primary" />
          Questions &amp; Comments
          {count > 0 && <span className="text-xs font-normal text-muted-foreground">({count})</span>}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          {/* Composer */}
          {user ? (
            <div className="space-y-2">
              <MentionTextarea
                value={text}
                onChange={setText}
                placeholder="Ask a question about this campaign…"
                rows={2}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                onMention={pk => mentioned.add(pk)}
              />
              <div className="flex justify-end">
                <button
                  onClick={handlePost}
                  disabled={posting || !text.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-bold disabled:opacity-40 hover:bg-primary/90 transition-colors"
                >
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Post
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowLoginDialog(true)}
              className="w-full rounded-xl border border-dashed border-border px-3 py-3 text-sm text-muted-foreground hover:border-primary/40 transition-colors"
            >
              Log in to ask a question
            </button>
          )}

          {/* Thread */}
          {loading && count === 0 ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading comments…
            </div>
          ) : count === 0 ? (
            <p className="text-xs text-muted-foreground py-1">
              No questions yet — be the first to ask.
            </p>
          ) : (
            <div className="space-y-3">
              {topLevel.map(ev => (
                <CommentItem
                  key={ev.id}
                  comment={ev}
                  replies={childrenOf.get(ev.id) ?? []}
                  childrenOf={childrenOf}
                  creatorPubkey={creatorPubkey}
                  onReply={publishComment}
                  canReply={!!user}
                  requireLogin={() => setShowLoginDialog(true)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── A single comment (with a one-level reply composer) ──────────────

function CommentItem({
  comment,
  replies,
  childrenOf,
  creatorPubkey,
  onReply,
  canReply,
  requireLogin,
  depth = 0,
}: {
  comment: Event;
  replies: Event[];
  childrenOf: Map<string, Event[]>;
  creatorPubkey: string;
  onReply: (body: string, parent: Event) => Promise<void>;
  canReply: boolean;
  requireLogin: () => void;
  depth?: number;
}) {
  const { profile } = useNostrProfile(comment.pubkey);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const name = profile?.display_name || profile?.name || comment.pubkey.slice(0, 8) + '…';
  const picture = safeUrl(profile?.picture);
  const isOP = comment.pubkey === creatorPubkey;
  const profileHref = `/profile/${comment.pubkey}`;

  const submitReply = useCallback(async () => {
    const body = replyText.trim();
    if (!body) return;
    if (!canReply) { requireLogin(); return; }
    setSending(true);
    try {
      await onReply(body, comment);
      setReplyText('');
      setReplying(false);
    } finally {
      setSending(false);
    }
  }, [replyText, canReply, requireLogin, onReply, comment]);

  return (
    <div className={depth > 0 ? 'pl-3 border-l-2 border-border/60' : ''}>
      <div className="flex gap-2.5">
        <Link to={profileHref} className="shrink-0 no-navigate">
          {picture ? (
            <img src={picture} alt="" loading="lazy" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-muted" />
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={profileHref} className="text-sm font-semibold hover:underline no-navigate truncate">
              {name}
            </Link>
            {isOP && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-bold">
                <Crown className="w-3 h-3" /> Creator
              </span>
            )}
            <span className="text-[11px] text-muted-foreground">· {timeAgo(comment.created_at)}</span>
          </div>
          <div className="text-sm mt-0.5 break-words">
            <NoteContent content={comment.content} tags={comment.tags} />
          </div>

          <div className="mt-1">
            <button
              onClick={() => (canReply ? setReplying(r => !r) : requireLogin())}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition-colors"
            >
              <CornerDownRight className="w-3 h-3" /> Reply
            </button>
          </div>

          {replying && (
            <div className="mt-2 space-y-2">
              <MentionTextarea
                value={replyText}
                onChange={setReplyText}
                placeholder={`Reply to ${name}…`}
                rows={2}
                autoFocus
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setReplying(false); setReplyText(''); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitReply}
                  disabled={sending || !replyText.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold disabled:opacity-40 hover:bg-primary/90 transition-colors"
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Reply
                </button>
              </div>
            </div>
          )}

          {/* Nested replies */}
          {replies.length > 0 && (
            <div className="mt-3 space-y-3">
              {replies
                .slice()
                .sort((a, b) => a.created_at - b.created_at)
                .map(r => (
                  <CommentItem
                    key={r.id}
                    comment={r}
                    replies={childrenOf.get(r.id) ?? []}
                    childrenOf={childrenOf}
                    creatorPubkey={creatorPubkey}
                    onReply={onReply}
                    canReply={canReply}
                    requireLogin={requireLogin}
                    depth={depth + 1}
                  />
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
